import axios from "../AxiosInterCeptor/AxiosInterCeptor";
import { ToastError, ToastSuccess } from "../UiComponents/Toaster/Toast";

const URL = process.env.React_App_BaseUrl;

const GetClientCompanyWallet = () => {
  return axios
    .get(`${URL}/clientWallet`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
        return { data: { companies: [], rows: [] } };
      }
      return res;
    })
    .catch((error) => {
      console.log(error);
      return { data: { companies: [], rows: [] } };
    });
};

const GetClientWalletDetail = (clientId) => {
  return axios
    .get(`${URL}/clientWallet/${clientId}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
        return null;
      }
      return res.data;
    })
    .catch((error) => {
      console.log(error);
      return null;
    });
};

const GetClientWalletTransactions = (clientId, companyName) => {
  return axios
    .get(`${URL}/clientWallet/${clientId}/transactions`, {
      params: companyName ? { companyName } : {},
    })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
        return [];
      }
      return res.data || [];
    })
    .catch((error) => {
      console.log(error);
      return [];
    });
};

// The statement: one entry per policy event (or per ledger row with grouped=false), a page (1-based) at a time. Never
// toasts and never throws: a failure resolves to { error, message } so the table can show it with a Retry.
const GetClientWalletStatement = (clientId, { companyName, page = 1, limit = 25, grouped = true } = {}) =>
  axios
    .get(`${URL}/clientWallet/${clientId}/statement`, {
      params: { page, limit, grouped: grouped ? "true" : "false", ...(companyName ? { companyName } : {}) },
    })
    .then((response) => response.data || { error: true, message: "Empty response" })
    .catch((error) => ({
      error: true,
      message: error?.response?.data?.message || error?.message || "Could not load the statement",
    }));

const PostClientWalletTopup = (recData) => {
  return axios
    .post(`${URL}/clientWallet/topup`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
        return res;
      }
      ToastSuccess(res.message || "Wallet Topup Successful");
      return res;
    })
    .catch((error) => {
      ToastError(error?.response?.data?.message || "Could not update the wallet");
      throw error;
    });
};

const PostClientWalletRemove = (recData) => {
  return axios
    .post(`${URL}/clientWallet/remove`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
        return res;
      }
      ToastSuccess(res.message || "Amount Removed Successfully");
      return res;
    })
    .catch((error) => {
      ToastError(error?.response?.data?.message || "Could not update the wallet");
      throw error;
    });
};

// Used by the Online Policy form to show a live sufficiency check while
// filling PA Cover. Reachable by branchManager/operator/user logins too, not
// just CLIENT — see Routes/routes.js walletBalanceReader on the backend.
// Silent on failure (no toast): this fires on every relevant field change
// while filling a form, and a transient error here shouldn't interrupt
// typing — the authoritative check happens server-side at Create time.
const GetClientWalletCompanyBalance = (clientId, companyName) => {
  return axios
    .get(`${URL}/clientWallet/${clientId}/companyBalance`, { params: { companyName } })
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.message);
        return { error: true, data: { balance: 0 } };
      }
      return res;
    })
    .catch((error) => {
      console.log(error);
      return { error: true, data: { balance: 0 } };
    });
};

// Used only while EDITING an Online Policy. The backend's edit flow refunds
// whatever this policy already has outstanding before re-debiting from the
// edited data — this fetches that same breakdown so the frontend's balance
// check can add it back onto the current balance rather than comparing the
// edited amount against a raw balance that doesn't yet reflect the refund
// that's about to happen. Silent on failure, same reasoning as the balance
// check above — treated as "nothing outstanding" rather than blocking.
const GetPolicyWalletOutstanding = (clientId, policyId) => {
  return axios
    .get(`${URL}/clientWallet/${clientId}/policyOutstanding/${policyId}`)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        console.log(res.message);
        return { error: true, data: [] };
      }
      return res;
    })
    .catch((error) => {
      console.log(error);
      return { error: true, data: [] };
    });
};

const PostClientWalletMinimumBalance = (recData) => {
  return axios
    .post(`${URL}/clientWallet/minimumBalance`, recData)
    .then((response) => {
      const res = response.data;
      if (res.error) {
        ToastError(res.message);
        return res;
      }
      ToastSuccess(res.message || "Minimum Balance Updated");
      return res;
    })
    .catch((error) => {
      ToastError(error?.response?.data?.message || "Could not update the minimum balance");
      throw error;
    });
};

export {
  GetClientCompanyWallet,
  GetClientWalletDetail,
  GetClientWalletTransactions,
  GetClientWalletStatement,
  GetClientWalletCompanyBalance,
  GetPolicyWalletOutstanding,
  PostClientWalletTopup,
  PostClientWalletRemove,
  PostClientWalletMinimumBalance,
};
